<template>
  <v-card style="min-width:650px;" elevation="0">
    <v-list class="branded-background">
      <v-list-tile avatar>
        <v-list-tile-avatar>
          <v-icon color="white">playlist_play</v-icon>
        </v-list-tile-avatar>
        <v-list-tile-content>
          <v-list-tile-title class="modal-title">
            <span class="text-white text-truncate-popover">Add tags to "{{playlist.name}}"</span>
          </v-list-tile-title>
        </v-list-tile-content>
      </v-list-tile>
    </v-list>
    <v-divider></v-divider>
    <v-card
      elevation="0"
      class="px-3">
      <v-card-text>
      <v-combobox
        :color="activeColor"
        v-model="localPlaylist.keywords"
        :items="tags.keywords"
        :rules="rules.tags"
        :search-input.sync="tagSearchString"
        label="Keywords/Tags"
        chips
        flat
        clearable
        multiple
        ref="keywords"
        @input="clearTagSearchString"
        :loading="isSearching || (isLoading && selectedField === 'keywords')"
        :success-messages="message.keywords"
      >
        <template slot="selection" slot-scope="{ item, index }">
          <v-chip
            :style="getTagStyles('user-generated', item)"
            close
            @input="handleChipRemoval({
              index,
              type: 'keywords',
            })"
          >
            <strong>{{ item }}</strong>
          </v-chip>
        </template>
      </v-combobox>
      <v-autocomplete
        :color="activeColor"
        class="select-overflow"
        v-model="localPlaylist.age"
        :items="tags.age"
        label="Target Audience"
        chips
        flat
        clearable
        multiple
        ref="age"
        :loading="isLoading && selectedField === 'age'"
        :success-messages="message.age"
        @change="toggleMenu('age')"
      >
        <template slot="selection" slot-scope="{ item, index }">
          <v-chip
            :style="getTagStyles('age', item)"
            close
            @input="handleChipRemoval({
              index,
              type: 'age',
            })"
          >
            <strong>
              {{ item }}
            </strong>&nbsp;
          </v-chip>
        </template>
      </v-autocomplete>
      <v-autocomplete
        :color="activeColor"
        class="select-overflow"
        v-model="tagType"
        :items="tags.subject"
        label="Subject"
        chips
        flat
        clearable
        multiple
        ref="subject"
        :loading="isLoading && selectedField === 'subject'"
        :success-messages="message.subject"
        @change="toggleMenu('subject')"
      >
        <template slot="selection" slot-scope="{ item, index }">
          <v-chip
            :style="getTagStyles('subject', item)"
            close
            @input="handleChipRemoval({
              index,
              type: 'subject',
            })"
          >
            <strong>
              {{ item }}
            </strong>&nbsp;
          </v-chip>
        </template>
      </v-autocomplete>
      </v-card-text>
    </v-card>
    <v-divider></v-divider>
    <v-card-actions class="row-space" style="height:56px">
      <v-btn
        flat
        class="branded-grey"
        @click="setDashModal(null)">
        Cancel
      </v-btn>
      <div class="row-center">
        <v-btn
          class="branded-background text-white"
          @click="formatDataForSave()"
          :loading="playlistModalLoading">
          Save
        </v-btn>
      </div>
    </v-card-actions>
  </v-card>
</template>

<script>
/* global _ */
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';
import bannedTags from '../../../js/bannedTags';
import _ from 'lodash';

export default {
  name: 'PlaylistTags',
  props: {
    activeColor: {
      type: String,
      default: '#2196F3',
    },
    playlist: {
      type: Object,
    },
  },
  data: () => ({
    isLoading: false,
    isSearching: false,
    localPlaylist: {
      age: [],
      keywords: [],
      subject: [],
    },
    message: {
      name: '',
      learningObjective: '',
      keywords: '',
      age: '',
      subject: '',
    },
    originalPlaylist: {},
    referencePlaylist: {},
    rules: {
      name: [
        (name) => !!(name && name.length) || 'This field is required.',
      ],
      tags: [
        (tags) => tags.every((tag) => bannedTags.indexOf(tag) === -1) || 'Inappropriate tags are not allowed',
        (tags) => tags.every((tag) => tag.length <= 255) || 'Tags must be less than 255 characters',
      ],
    },
    selectedField: null,
    tagMatchesSearched: false,
    tagSearchString: null,
  }),
  computed: {
    ...mapState('instructors', [
      'playlistModalLoading',
      'tags',
    ]),
    ...mapGetters('instructors', [
      'instructorAjaxing',
      'queryParamsInstructors',
    ]),
    tagStyles() {
      const tagStyles = {};
      for (const tag of this.uniqueTagTypes) {
        tagStyles[tag.type] = {
          backgroundColor: tag.hex,
          color: `${Color(tag.hex).darken(0.6)} !important`,
          border: `1px solid ${Color(tag.hex).darken(0.6)} !important`,
        };
      }
      return tagStyles;
    },
    tagType: {
      get() {
        return this.localPlaylist.subject;
      },
      set(newValue) {
        this.localPlaylist.subject = newValue;
      },
    },
    uniqueTagTypes() {
      return _.uniqBy(_.get(this, 'tags.rawData', []), 'type');
    },
  },
  methods: {
    ...mapMutations([
      'setDashModal',
      'triggerSnackbar',
    ]),
    ...mapActions('instructors', [
      'savePlaylistTags',
      'getTags',
    ]),
    applyTagSearchString: _.debounce(function debouncedApplyTagSearchString(searchString) {
      if (_.isNil(searchString)) searchString = '';
      // reset tag matches search if input is less than 4 characters
      if (searchString.length < 3) this.tagMatchesSearched = false;
      // Trigger tag matches search when input >= 3 characters and we haven't searched yet
      if (searchString.length >= 3 && !this.tagMatchesSearched) {
        const data = { searchString, isSearch: true };
        this.tagMatchesSearched = true;
        this.getTags(data);
      }
    }, 275),
    clearTagSearchString() {
      this.$set(this, 'tagSearchString', '');
    },
    async formatDataForSave() {
      const playlist = {
        id: this.localPlaylist.id,
        addTags: [],
        deleteTags: [],
      };
      for (const playlistTag of this.referencePlaylist.playlistTags) {
        if (
          playlistTag.tag.type === 'user-generated'
          && this.localPlaylist.keywords.indexOf(playlistTag.text) < 0
        ) playlist.deleteTags.push(playlistTag.playlistTagId);
        if (
          playlistTag.tag.type === 'age'
          && this.localPlaylist.age.indexOf(playlistTag.tag.value) < 0
        ) playlist.deleteTags.push(playlistTag.playlistTagId);
        if (
          playlistTag.tag.type === 'subject'
          && this.localPlaylist.subject.indexOf(playlistTag.tag.value) < 0
        ) playlist.deleteTags.push(playlistTag.playlistTagId);
      }
      for (const keyword of this.localPlaylist.keywords) {
        if (this.originalPlaylist.keywords.indexOf(keyword.trim()) >= 0) continue;
        playlist.addTags.push({
          playlistId: this.localPlaylist.id,
          tagId: 1,
          text: keyword.trim(),
        });
      }
      for (const ageTag of this.localPlaylist.age) {
        if (this.originalPlaylist.age.indexOf(ageTag) >= 0) continue;
        for (const tag of this.tags.rawData) {
          if (
            tag.type === 'age'
            && tag.value === ageTag
          ) {
            playlist.addTags.push({
              playlistId: this.localPlaylist.id,
              tagId: tag.id,
            });
          }
        }
      }
      for (const subjectTag of this.localPlaylist.subject) {
        if (this.originalPlaylist.subject.indexOf(subjectTag) >= 0) continue;
        for (const tag of this.tags.rawData) {
          if (
            tag.type === 'subject'
            && tag.value === subjectTag
          ) {
            playlist.addTags.push({
              playlistId: this.localPlaylist.id,
              tagId: tag.id,
            });
          }
        }
      }
      const updatedPlaylistData = await this.savePlaylistTags(playlist);
      this.referencePlaylist.tags = [
        ...(
          _.get(updatedPlaylistData, 'playlistTags')
        )
      ];
      await this.$set(this, 'originalPlaylist', _.cloneDeep(this.localPlaylist));
    },
    getTagStyles(key, item) {
      if (
        bannedTags.includes(item.toLowerCase())
        || item.length >= 255
      ) {
        return {
          backgroundColor: '#EF5350',
          color: 'white !important',
          border: '1px solid #602120 !important',
        };
      }
      return this.tagStyles[key];
    },
    handleChipRemoval({
      index,
      type,
    } = {}) {
      if (type === 'keywords') this.localPlaylist.keywords.splice(index, 1);
      else this.$delete(this.localPlaylist[type], index);
    },
    initPlaylist() {
      this.$set(this, 'referencePlaylist', _.cloneDeep(this.playlist));
      // eslint-disable-next-line guard-for-in
      for (const key in this.referencePlaylist) {
        this.localPlaylist[key] = this.referencePlaylist[key];
        if (key === 'playlistTags') {
          for (const playlistTag of this.referencePlaylist[key]) {
            if (
              playlistTag.tag.type === 'user-generated'
              && !this.localPlaylist.keywords.includes(playlistTag.text)
            ) this.localPlaylist.keywords.push(playlistTag.text);
            if (
              playlistTag.tag.type === 'age'
              && !this.localPlaylist.age.includes(playlistTag.tag.value)
            ) this.localPlaylist.age.push(playlistTag.tag.value);
            if (
              playlistTag.tag.type === 'subject'
              && !this.localPlaylist.subject.includes(playlistTag.tag.value)
            ) this.localPlaylist.subject.push(playlistTag.tag.value);
          }
        }
      };
      this.getTags({ isSearch: true });
      this.$set(this, 'originalPlaylist', _.cloneDeep(this.localPlaylist));
    },
    toggleMenu(type) {
      if (
        this.localPlaylist[type].length
        && this.$refs[type]
      ) {
        this.$refs[type].blur();
      }
    },
  },
  watch: {
    tagSearchString(n) {
      this.applyTagSearchString(n);
    },
  },
  created() {
    this.initPlaylist();
  },
};
</script>


